"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGetProducts = void 0;
const react_1 = require("react");
const react_2 = require("@apollo/client/react");
const chunk_utils_1 = require("./chunk.utils");
const fetch_product_chunk_batch_utils_1 = require("./fetch-product-chunk-batch.utils");
const pagination_utils_1 = require("./pagination.utils");
const product_edge_utils_1 = require("./product-edge.utils");
const query_variables_utils_1 = require("./query-variables.utils");
const client_utils_1 = require("../../../client.utils");
const get_products_query_1 = require("../../gql/get-products.query");
const hooks_1 = require("../../../../../hooks");
const localization_1 = require("../../../../../localization");
const store_1 = require("../../../../../store");
const useGetProducts = ({ catalogId, productTreeNodeId, options, filter, sort, productTextAttributesFilter, productMeasuresFilter, skipMeasures, reviewStatus, pageSize, chunkSize, includeLabelExplanation = true, shouldFetchLockedCount = false, skipLabelValidation = true, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const client = (0, react_2.useApolloClient)();
    const { addError } = (0, hooks_1.useNotification)();
    const { formatMessage: t } = (0, localization_1.useLocalization)();
    const productTreeId = (0, store_1.useProductTreeId)();
    const shouldFetchReviewStatus = !!reviewStatus;
    const lockedProductsFilter = (0, react_1.useMemo)(() => (0, query_variables_utils_1.getLockedProductsFilter)(shouldFetchLockedCount, filter), [shouldFetchLockedCount, filter]);
    const labelValidationFilter = (0, react_1.useMemo)(() => (0, query_variables_utils_1.getLabelValidationFilter)(productTextAttributesFilter, skipLabelValidation), [productTextAttributesFilter, skipLabelValidation]);
    const shouldSkipProductsQuery = (0, query_variables_utils_1.getShouldSkipProductsQuery)(catalogId, productTreeNodeId, productTreeId, options === null || options === void 0 ? void 0 : options.skip);
    const shouldChunkRows = (0, pagination_utils_1.getShouldChunkRows)(pageSize, chunkSize);
    const firstChunkSize = (0, pagination_utils_1.getFirstChunkSize)(pageSize, chunkSize);
    const currentRequestKeyRef = (0, react_1.useRef)();
    const activeChunkStateRef = (0, react_1.useRef)();
    const [chunkState, setChunkState] = (0, react_1.useState)();
    const [isLoadingNextChunk, setIsLoadingNextChunk] = (0, react_1.useState)(false);
    const [hasChunkLoadError, setHasChunkLoadError] = (0, react_1.useState)(false);
    const [chunkLoadError, setChunkLoadError] = (0, react_1.useState)();
    const [chunkLoadErrorProductCount, setChunkLoadErrorProductCount] = (0, react_1.useState)(0);
    const { data, loading, variables, previousData, error, networkStatus, refetch, } = (0, react_2.useQuery)(get_products_query_1.GET_PRODUCTS, {
        skip: shouldSkipProductsQuery,
        variables: {
            catalogId,
            productTreeNodeId,
            productTreeId,
            filter,
            sort,
            labelFilter: productTextAttributesFilter,
            measureFilter: productMeasuresFilter,
            first: firstChunkSize,
            before: null,
            after: null,
            last: null,
            skipMeasures,
            shouldFetchReviewStatus,
            reviewStatusAttribute: reviewStatus === null || reviewStatus === void 0 ? void 0 : reviewStatus.attribute,
            reviewStatusAttributeType: reviewStatus === null || reviewStatus === void 0 ? void 0 : reviewStatus.attributeType,
            shouldFetchLockedCount,
            lockedProductsFilter,
            labelValidationFilter,
            skipLabelValidation,
            includeLabelExplanation,
        },
        fetchPolicy: (_a = options === null || options === void 0 ? void 0 : options.fetchPolicy) !== null && _a !== void 0 ? _a : 'no-cache',
        errorPolicy: 'all', // Return both data and errors, don't throw on cache reset
        notifyOnNetworkStatusChange: true,
    });
    const requestKey = (0, react_1.useMemo)(() => (variables ? JSON.stringify(variables) : undefined), [variables]);
    (0, react_1.useEffect)(() => {
        if (!(0, client_utils_1.isQueryRefetching)(networkStatus))
            return;
        activeChunkStateRef.current = undefined;
        setIsLoadingNextChunk(false);
        setChunkLoadError(undefined);
        setChunkLoadErrorProductCount(0);
    }, [networkStatus]);
    (0, react_1.useEffect)(() => {
        var _a;
        currentRequestKeyRef.current = requestKey;
        const productPresentations = (_a = data === null || data === void 0 ? void 0 : data.productTreeNode) === null || _a === void 0 ? void 0 : _a.productPresentationsNew;
        if (!requestKey || !productPresentations) {
            setChunkState(undefined);
            setIsLoadingNextChunk(false);
            setHasChunkLoadError(false);
            setChunkLoadError(undefined);
            setChunkLoadErrorProductCount(0);
            return;
        }
        const nextChunkState = {
            requestKey,
            edges: productPresentations.edges,
            pageInfo: productPresentations.pageInfo,
            requestedRowCount: firstChunkSize,
        };
        activeChunkStateRef.current = nextChunkState;
        setChunkState(nextChunkState);
        setIsLoadingNextChunk(false);
        setHasChunkLoadError(false);
        setChunkLoadError(undefined);
        setChunkLoadErrorProductCount(0);
    }, [data, firstChunkSize, requestKey]);
    const productPresentations = (0, react_1.useMemo)(() => {
        var _a;
        const currentProductPresentations = (_a = data === null || data === void 0 ? void 0 : data.productTreeNode) === null || _a === void 0 ? void 0 : _a.productPresentationsNew;
        if (!currentProductPresentations || !chunkState || chunkState.requestKey !== requestKey) {
            return currentProductPresentations;
        }
        return Object.assign(Object.assign({}, currentProductPresentations), { edges: chunkState.edges, pageInfo: Object.assign(Object.assign({}, currentProductPresentations.pageInfo), { endCursor: chunkState.pageInfo.endCursor }) });
    }, [chunkState, data, requestKey]);
    const { totalCount: prevTotalCount = 0, pageInfo: prevPageInfo } = (_c = (_b = previousData === null || previousData === void 0 ? void 0 : previousData.productTreeNode) === null || _b === void 0 ? void 0 : _b.productPresentationsNew) !== null && _c !== void 0 ? _c : {};
    const { totalCount = 0, pageInfo } = productPresentations !== null && productPresentations !== void 0 ? productPresentations : {};
    const currentChunkEdges = productPresentations === null || productPresentations === void 0 ? void 0 : productPresentations.edges;
    const targetPageRowCount = (0, pagination_utils_1.getTargetPageRowCount)(pageSize, totalCount, pageInfo);
    const requestedRowCount = (0, chunk_utils_1.getRequestedRowCount)(chunkState);
    const canFetchNextChunk = (0, chunk_utils_1.getShouldFetchNextChunk)({
        hasChunkLoadError,
        networkStatus,
        shouldChunkRows,
        currentChunkCount: (_d = currentChunkEdges === null || currentChunkEdges === void 0 ? void 0 : currentChunkEdges.length) !== null && _d !== void 0 ? _d : 0,
        targetPageRowCount,
        requestedRowCount,
        targetPageRequestRowCount: targetPageRowCount,
        endCursor: pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.endCursor,
        isLoadingNextChunk,
    });
    const isPageRowsSyncing = canFetchNextChunk || isLoadingNextChunk;
    const arePageRowsIncomplete = (0, chunk_utils_1.getArePageRowsIncomplete)({
        canFetchNextChunk,
        isLoadingNextChunk,
        chunkLoadErrorProductCount,
    });
    const fetchNextChunk = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        const nextChunkContext = (0, chunk_utils_1.getNextChunkContext)({
            canFetchNextChunk,
            chunkState,
            firstChunkSize,
            pageInfo,
            requestKey,
            targetPageRequestRowCount: targetPageRowCount,
            variables,
        });
        if (!nextChunkContext)
            return;
        const { currentRequestKey, currentChunkState, nextChunkRequestVariables } = nextChunkContext;
        setIsLoadingNextChunk(true);
        try {
            const nextProductPresentations = yield (0, fetch_product_chunk_batch_utils_1.fetchProductChunkBatch)({
                client,
                variables: nextChunkContext.variables,
                nextChunkRequestVariables,
            });
            if (!(0, chunk_utils_1.getIsActiveChunkState)({
                activeChunkState: activeChunkStateRef.current,
                activeRequestKey: currentRequestKeyRef.current,
                chunkState: currentChunkState,
                requestKey: currentRequestKey,
            }))
                return;
            const nextChunkState = (0, chunk_utils_1.getNextChunkState)(currentRequestKey, currentChunkState, nextProductPresentations, nextChunkRequestVariables);
            activeChunkStateRef.current = nextChunkState;
            setChunkState(nextChunkState);
        }
        catch (chunkError) {
            if ((0, chunk_utils_1.getIsActiveChunkState)({
                activeChunkState: activeChunkStateRef.current,
                activeRequestKey: currentRequestKeyRef.current,
                chunkState: currentChunkState,
                requestKey: currentRequestKey,
            })) {
                const nextChunkLoadError = (0, fetch_product_chunk_batch_utils_1.getChunkLoadError)(chunkError);
                setHasChunkLoadError(true);
                setChunkLoadError(nextChunkLoadError);
                setChunkLoadErrorProductCount((0, chunk_utils_1.getCancelledChunkRequestProductCount)({
                    requestedRowCount: currentChunkState.requestedRowCount,
                    targetPageRequestRowCount: targetPageRowCount,
                }));
                addError(t({ key: 'PRODUCT_EDITOR_TABLE_LOADING_ERROR' }), nextChunkLoadError.message);
            }
        }
        finally {
            if (currentRequestKeyRef.current === currentRequestKey) {
                setIsLoadingNextChunk(false);
            }
        }
    }), [
        canFetchNextChunk,
        addError,
        chunkState,
        client,
        firstChunkSize,
        pageInfo,
        requestKey,
        targetPageRowCount,
        t,
        variables,
    ]);
    const retryChunkLoad = (0, react_1.useCallback)(() => {
        if (!chunkLoadError)
            return;
        setHasChunkLoadError(false);
        setChunkLoadError(undefined);
        setChunkLoadErrorProductCount(0);
    }, [chunkLoadError]);
    (0, react_1.useEffect)(() => {
        if (!canFetchNextChunk)
            return;
        void fetchNextChunk();
    }, [canFetchNextChunk, fetchNextChunk]);
    const products = (0, react_1.useMemo)(() => {
        var _a, _b, _c;
        const currentEdges = (_a = productPresentations === null || productPresentations === void 0 ? void 0 : productPresentations.edges) !== null && _a !== void 0 ? _a : [];
        const prevEdges = (_c = (_b = previousData === null || previousData === void 0 ? void 0 : previousData.productTreeNode) === null || _b === void 0 ? void 0 : _b.productPresentationsNew) === null || _c === void 0 ? void 0 : _c.edges;
        // Guard 1: incomplete Product objects in cache (missing base fields like id).
        // A real Product always has an id — fall back to previousData so the table
        // shows stale-but-complete rows rather than crashing.
        const stableEdges = (0, product_edge_utils_1.getStableProductEdges)(currentEdges, prevEdges, data != null);
        // Guard 2: cache eviction gap.
        // refetchProductList evicts productPresentationsNew from the Apollo cache so
        // the next response is always fresh. Between the eviction broadcast and the
        // networkStatus transitioning to 'refetch', there is one render where
        // data === undefined (cache miss) but networkStatus is still 'ready'.
        // isLoading is therefore false, products would be [], and the DataGrid renders
        // the "no data" overlay for a single frame. Using previousData here shows
        // stale-but-correct rows during that gap instead.
        // For variable changes (e.g. navigating to a new node), useTableLoadingState's
        // !hasSettled guard holds loading=true for the first render cycle, so stale rows
        // are covered by the loading overlay and the user does not see the wrong node's data.
        return stableEdges.map((edge) => (0, product_edge_utils_1.getProductFromPresentationEdge)(edge, skipLabelValidation));
    }, [data, previousData, productPresentations, skipLabelValidation]);
    const lockedProductsCount = (_g = (_f = (_e = data === null || data === void 0 ? void 0 : data.productTreeNode) === null || _e === void 0 ? void 0 : _e.lockedProductPresentations) === null || _f === void 0 ? void 0 : _f.totalCount) !== null && _g !== void 0 ? _g : 0;
    const isLoading = shouldSkipProductsQuery || (0, client_utils_1.isQueryLoadingOrRefetching)(networkStatus);
    return {
        lastVariables: variables,
        refetch: (variables) => refetch((0, pagination_utils_1.getFirstChunkVariables)(variables, shouldChunkRows, firstChunkSize)),
        networkStatus,
        isLoading,
        isPageRowsSyncing,
        chunkLoadError,
        chunkLoadErrorProductCount,
        canRetryChunkLoad: Boolean(chunkLoadError),
        retryChunkLoad,
        arePageRowsIncomplete,
        pageInfo: loading ? pageInfo || prevPageInfo : pageInfo,
        totalCount: loading ? totalCount || prevTotalCount : totalCount,
        lockedProductsCount,
        error: error !== null && error !== void 0 ? error : chunkLoadError,
        nodeTitle: ((_h = data === null || data === void 0 ? void 0 : data.productTreeNode) === null || _h === void 0 ? void 0 : _h.title) || '',
        products,
    };
};
exports.useGetProducts = useGetProducts;
